import { lazy, Suspense, useEffect } from "react";
import { Helmet } from "react-helmet-async";
import { useIsMobile } from "@/hooks/use-mobile";
import { MobileApp } from "@/components/mobile/MobileApp";
import { TopBar } from "@/components/TopBar";
import { Header } from "@/components/Header";
import { Hero } from "@/components/Hero";
import { KeyBenefits } from "@/components/KeyBenefits";
import { HowItWorks } from "@/components/HowItWorks";
import { VideoSection } from "@/components/VideoSection";
import { PhotoMosaic } from "@/components/PhotoMosaic";
import { TrainSection } from "@/components/TrainSection";
import { BenefitsGallery } from "@/components/BenefitsGallery";

import { Footer } from "@/components/Footer";

// Below-the-fold sections are code-split so their JS is fetched after first paint.
const ProblemSection = lazy(() => import("@/components/ProblemSection").then((m) => ({ default: m.ProblemSection })));
const SolutionSection = lazy(() => import("@/components/SolutionSection").then((m) => ({ default: m.SolutionSection })));
const TechnologySection = lazy(() => import("@/components/TechnologySection").then((m) => ({ default: m.TechnologySection })));
const CompetitionSection = lazy(() => import("@/components/CompetitionSection").then((m) => ({ default: m.CompetitionSection })));
const FlipBookSection = lazy(() => import("@/components/FlipBookSection").then((m) => ({ default: m.FlipBookSection })));
const MarketSection = lazy(() => import("@/components/MarketSection").then((m) => ({ default: m.MarketSection })));
const BusinessSection = lazy(() => import("@/components/BusinessSection").then((m) => ({ default: m.BusinessSection })));
const SecuritySection = lazy(() => import("@/components/SecuritySection").then((m) => ({ default: m.SecuritySection })));
const ComplianceSection = lazy(() => import("@/components/ComplianceSection").then((m) => ({ default: m.ComplianceSection })));
const ContactSection = lazy(() => import("@/components/ContactSection").then((m) => ({ default: m.ContactSection })));
const InvestButton = lazy(() => import("@/components/InvestButton").then((m) => ({ default: m.InvestButton })));

const Index = () => {
  const isMobile = useIsMobile();

  useEffect(() => {
    if (isMobile) return;
    const observerCallback = (entries: IntersectionObserverEntry[]) => {
      entries.forEach((entry) => {
        if (entry.isIntersecting) {
          entry.target.classList.add("opacity-100", "translate-y-0");
          entry.target.classList.remove("opacity-0", "translate-y-8");
        }
      });
    };

    const observer = new IntersectionObserver(observerCallback, {
      threshold: 0.1,
      rootMargin: "50px",
    });

    document.querySelectorAll(".reveal").forEach((el) => {
      el.classList.add("opacity-0", "translate-y-8", "transition-all", "duration-700");
      observer.observe(el);
    });

    return () => observer.disconnect();
  }, [isMobile]);

  return (
    <div className="min-h-screen">
      <Helmet>
        <title>datasyteAI™ + AIMediQ™ | Expert-trained Code Blue Decision Support</title>
        <meta
          name="description"
          content="Expert-trained Code Blue decision support with AIMediQ™, the AI-powered Code Cart by datasyteAI™. Built for clarity under pressure, with quantum-resistant security in progress."
        />
        <link rel="canonical" href="https://datasyte.ai/" />
        <meta property="og:url" content="https://datasyte.ai/" />
        <meta property="og:title" content="datasyteAI™ + AIMediQ™ | Expert-trained Code Blue Decision Support" />
        <meta
          property="og:description"
          content="Expert-trained Code Blue decision support with AIMediQ™, the AI-powered Code Cart by datasyteAI™."
        />
        <meta name="twitter:title" content="datasyteAI™ + AIMediQ™ | Expert-trained Code Blue Decision Support" />
        <meta
          name="twitter:description"
          content="Expert-trained Code Blue decision support with AIMediQ™, the AI-powered Code Cart by datasyteAI™."
        />
      </Helmet>
      {isMobile ? (
        <MobileApp />
      ) : (
        <>
          <TopBar />
          <Header />
          <main>
            <Hero />
            <KeyBenefits />
            <HowItWorks />
            <VideoSection />
            <PhotoMosaic />
            <TrainSection />
            <BenefitsGallery />
            <Suspense fallback={<div className="min-h-[40vh]" />}>
              <ProblemSection />
              <SolutionSection />
              <TechnologySection />
              <CompetitionSection />
              <FlipBookSection />
              <MarketSection />
              <BusinessSection />
              <SecuritySection />
              <ContactSection />
              <InvestButton />
              <ComplianceSection />
            </Suspense>
          </main>
          <Footer />
        </>
      )}
    </div>

  );
};

export default Index;
